import React, { useState } from 'react';
import Layout from 'layout/Layout';
import { TiUserDelete } from 'react-icons/ti';
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetAllUsersQuery,
} from 'api/userApi';

const ManageUser = () => {
  const { data: users, isLoading, refetch } = useGetAllUsersQuery();
  const [createUser] = useCreateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
  });

  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrorMessage('');
  };

  const validateForm = () => {
    const { email, password } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return false;
    }

    return true;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createUser(formData).unwrap();
      refetch();
      setFormData({
        username: '',
        password: '',
        email: '',
        first_name: '',
        last_name: '',
      });
      setErrorMessage('');
    } catch (error) {
      console.error('Create user error:', error);
      setErrorMessage('Failed to create user. Please try again.');
    }
  };

  const handleDeleteUser = async (email) => {
    try {
      await deleteUser(email).unwrap();
      refetch();
    } catch (error) {
      console.error('Delete user error:', error);
    }
  };

  return (
    <Layout title="Manage Users" tagline="Manage your employees with ease">
      <div className="p-4 mt-16 max-w-4xl mx-auto w-full">
        {/* User Form */}
        <form
          onSubmit={handleCreateUser}
          className="space-y-4 border p-6 rounded-md bg-white shadow-md w-full"
        >
          <h2 className="text-xl font-semibold">Add New User</h2>

          {errorMessage && (
            <p className="text-red-600 text-sm">{errorMessage}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['username', 'email', 'first_name', 'last_name'].map((field) => (
              <input
                key={field}
                type="text"
                name={field}
                placeholder={field.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
                value={formData[field]}
                onChange={handleInputChange}
                className="w-full border border-gray-300 p-2 rounded text-sm"
                required
              />
            ))}

            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full md:w-1/2 border border-gray-300 p-2 rounded text-sm sm:col-span-2"
              required
            />
          </div>

          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Create User
          </button>
        </form>

        {/* User Table */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">All Users</h2>
          {isLoading ? (
            <p>Loading users...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-4 py-2 text-left">Username</th>
                    <th className="border px-4 py-2 text-left">Name</th>
                    <th className="border px-4 py-2 text-left">Email</th>
                    <th className="border px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map((user) => (
                    <tr key={user.email} className="hover:bg-gray-50">
                      <td className="border px-4 py-2 font-medium">{user.username}</td>
                      <td className="border px-4 py-2">{user.first_name} {user.last_name}</td>
                      <td className="border px-4 py-2">{user.email}</td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleDeleteUser(user.email)}
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 flex items-center gap-1 text-xs"
                        >
                          Delete
                          <TiUserDelete className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ManageUser;
